import React, { useState, useEffect, useRef } from 'react';
import classNames from 'classnames';
import { useLocation } from 'react-router-dom';
import { CSSTransition } from 'react-transition-group';

import { AppTopbar } from './AppTopbar';
import { AppFooter } from './AppFooter';
import { AppMenu } from './AppMenu';
import { AppConfig } from './AppConfig';
import { useTheme } from './useTheme';

import PrimeReact, { addLocale } from 'primereact/api';
import { Tooltip } from 'primereact/tooltip';

import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';

import { useSelector, useDispatch } from 'react-redux';

import 'primereact/resources/primereact.css';
import 'primeicons/primeicons.css';
import 'primeflex/primeflex.css';
import 'prismjs/themes/prism-coy.css';
import './assets/demo/flags/flags.css';
import './assets/demo/Demos.scss';
import './assets/layout/layout.scss';
import './App.scss';
import { addMessage, selectMessage } from './app/appSlice';

// PrimeReact só traz strings padrão em inglês (Dropdown, Calendar,
// DataTable etc.) — sem registrar um locale, mensagens como "No results
// found"/"No available options" aparecem em inglês em qualquer componente,
// independente do texto que a gente mesmo escreve (placeholder, título...).
addLocale('pt', {
    startsWith: 'Começa com',
    contains: 'Contém',
    notContains: 'Não contém',
    endsWith: 'Termina com',
    equals: 'Igual',
    notEquals: 'Diferente',
    noFilter: 'Sem filtro',
    lt: 'Menor que',
    lte: 'Menor ou igual a',
    gt: 'Maior que',
    gte: 'Maior ou igual a',
    dateIs: 'Data é',
    dateIsNot: 'Data não é',
    dateBefore: 'Data é anterior a',
    dateAfter: 'Data é posterior a',
    clear: 'Limpar',
    apply: 'Aplicar',
    matchAll: 'Corresponder a todos',
    matchAny: 'Corresponder a qualquer',
    addRule: 'Adicionar regra',
    removeRule: 'Remover regra',
    accept: 'Sim',
    reject: 'Não',
    choose: 'Escolher',
    upload: 'Enviar',
    cancel: 'Cancelar',
    dayNames: ['Domingo', 'Segunda-feira', 'Terça-feira', 'Quarta-feira', 'Quinta-feira', 'Sexta-feira', 'Sábado'],
    dayNamesShort: ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'],
    dayNamesMin: ['Do', 'Se', 'Te', 'Qu', 'Qu', 'Se', 'Sa'],
    monthNames: ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'],
    monthNamesShort: ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'],
    today: 'Hoje',
    weekHeader: 'Sem',
    firstDayOfWeek: 0,
    dateFormat: 'dd/mm/yy',
    weak: 'Fraca',
    medium: 'Média',
    strong: 'Forte',
    passwordPrompt: 'Digite uma senha',
    emptyFilterMessage: 'Nenhum resultado encontrado',
    searchMessage: '{0} resultados disponíveis',
    selectionMessage: '{0} itens selecionados',
    emptySelectionMessage: 'Nenhum item selecionado',
    emptySearchMessage: 'Nenhum resultado encontrado',
    emptyMessage: 'Nenhuma opção disponível',
    aria: {
        trueLabel: 'Verdadeiro',
        falseLabel: 'Falso',
        nullLabel: 'Não selecionado',
        star: '1 estrela',
        stars: '{star} estrelas',
        selectAll: 'Todos os itens selecionados',
        unselectAll: 'Todos os itens desmarcados',
        close: 'Fechar',
        previous: 'Anterior',
        next: 'Próximo',
        navigation: 'Navegação',
        scrollTop: 'Rolar para o topo',
        moveTop: 'Mover para o topo',
        moveUp: 'Mover para cima',
        moveDown: 'Mover para baixo',
        moveBottom: 'Mover para o final',
        moveToTarget: 'Mover para o destino',
        moveToSource: 'Mover para a origem',
        moveAllToTarget: 'Mover tudo para o destino',
        moveAllToSource: 'Mover tudo para a origem',
        pageLabel: 'Página {page}',
        firstPageLabel: 'Primeira página',
        lastPageLabel: 'Última página',
        nextPageLabel: 'Próxima página',
        prevPageLabel: 'Página anterior',
        rowsPerPageLabel: 'Linhas por página',
        jumpToPageDropdownLabel: 'Ir para a página',
        jumpToPageInputLabel: 'Ir para a página',
        selectRow: 'Linha selecionada',
        unselectRow: 'Linha desmarcada',
        expandRow: 'Linha expandida',
        collapseRow: 'Linha recolhida',
        showFilterMenu: 'Mostrar menu de filtro',
        hideFilterMenu: 'Ocultar menu de filtro',
        filterOperator: 'Operador do filtro',
        filterConstraint: 'Restrição do filtro',
        editRow: 'Editar linha',
        saveEdit: 'Salvar edição',
        cancelEdit: 'Cancelar edição',
        listView: 'Visualização em lista',
        gridView: 'Visualização em grade',
        slide: 'Slide',
        slideNumber: '{slideNumber}',
        zoomImage: 'Ampliar imagem',
        zoomIn: 'Aumentar zoom',
        zoomOut: 'Diminuir zoom',
        rotateRight: 'Girar para a direita',
        rotateLeft: 'Girar para a esquerda'
    }
});
PrimeReact.locale = 'pt';

const App = (props) => {
    const [layoutMode, setLayoutMode] = useState('static');
    const { theme, layoutColorMode, onColorModeChange, changeTheme, onToggleColorScheme } = useTheme();
    const [inputStyle, setInputStyle] = useState('outlined');
    const [ripple, setRipple] = useState(true);
    const [staticMenuInactive, setStaticMenuInactive] = useState(false);
    const [overlayMenuActive, setOverlayMenuActive] = useState(false);
    const [mobileMenuActive, setMobileMenuActive] = useState(false);
    const [mobileTopbarMenuActive, setMobileTopbarMenuActive] = useState(false);
    const copyTooltipRef = useRef();
    const location = useLocation();

    const toast = useRef();
    const message = useSelector(selectMessage);
    const dispatch = useDispatch();

    PrimeReact.ripple = true;

    let menuClick = false;
    let mobileTopbarMenuClick = false;

    useEffect(() => {
        if (mobileMenuActive) {
            addClass(document.body, "body-overflow-hidden");
        } else {
            removeClass(document.body, "body-overflow-hidden");
        }
    }, [mobileMenuActive]);

    useEffect(() => {
        copyTooltipRef && copyTooltipRef.current && copyTooltipRef.current.updateTargetEvents();
    }, [location]);

    const onInputStyleChange = (inputStyle) => {
        setInputStyle(inputStyle);
    }

    const onRipple = (e) => {
        PrimeReact.ripple = e.value;
        setRipple(e.value)
    }

    const onLayoutModeChange = (mode) => {
        setLayoutMode(mode)
    }

    const onWrapperClick = (event) => {
        if (!menuClick) {
            setOverlayMenuActive(false);
            setMobileMenuActive(false);
        }

        if (!mobileTopbarMenuClick) {
            setMobileTopbarMenuActive(false);
        }

        mobileTopbarMenuClick = false;
        menuClick = false;
    }

    const onToggleMenuClick = (event) => {
        menuClick = true;

        if (isDesktop()) {
            if (layoutMode === 'overlay') {
                if (mobileMenuActive === true) {
                    setOverlayMenuActive(true);
                }

                setOverlayMenuActive((prevState) => !prevState);
                setMobileMenuActive(false);
            }
            else if (layoutMode === 'static') {
                setStaticMenuInactive((prevState) => !prevState);
            }
        }
        else {
            setMobileMenuActive((prevState) => !prevState);
        }

        event.preventDefault();
    }

    const onSidebarClick = () => {
        menuClick = true;
    }

    const onMobileTopbarMenuClick = (event) => {
        mobileTopbarMenuClick = true;

        setMobileTopbarMenuActive((prevState) => !prevState);
        event.preventDefault();
    }

    const onMobileSubTopbarMenuClick = (event) => {
        mobileTopbarMenuClick = true;

        event.preventDefault();
    }

    const onMenuItemClick = (event) => {
        if (!event.item.items) {
            setOverlayMenuActive(false);
            setMobileMenuActive(false);
        }
    }
    const isDesktop = () => {
        return window.innerWidth >= 992;
    }

    const menu = [
        {
            label: 'Home',
            items: [
                { label: 'Dashboard', icon: 'pi pi-fw pi-chart-line', to: '/home' },
            ]
        },
        {
            label: 'Desempenho',
            items: [
                { label: 'Ranking de alunos', icon: 'pi pi-fw pi-chart-bar', to: '/students-ranking' },
                { label: 'Desempenho geral', icon: 'pi pi-fw pi-chart-pie', to: '/desempenho' },
                { label: 'Heatmap de desempenho', icon: 'pi pi-fw pi-table', to: '/desempenho/heatmap' },
                { label: 'Notificações de churn', icon: 'pi pi-fw pi-bell', to: '/churn-notifications' },
            ]
        },
        {
            label: 'Gestão',
            items: [
                { label: 'Gestão de usuários', icon: 'pi pi-fw pi-users', to: '/users' },
                { label: 'Gestão de produtos', icon: 'pi pi-fw pi-box', to: '/products' },
                { label: 'Gestão de módulos', icon: 'pi pi-fw pi-th-large', to: '/modules' },
                { label: 'Gestão de serviços', icon: 'pi pi-fw pi-briefcase', to: '/medical-services' },
                { label: 'Gestão de provas', icon: 'pi pi-fw pi-check-square', to: '/tests' },
                { label: 'Gestão de importações', icon: 'pi pi-fw pi-upload', to: '/importations' },
                { label: 'Gestão de parceiros', icon: 'pi pi-fw pi-building', to: '/partners' },
                { label: 'Política de Privacidade e Termos', icon: 'pi pi-fw pi-file-edit', to: '/legal-documents' }
            ]
        },
    ];

    if (process.env.REACT_APP_SHOW_DEMOS === 'true') {
        const demo = [
            {
                label: 'UI Components', icon: 'pi pi-fw pi-sitemap',
                items: [
                    { label: 'Form Layout', icon: 'pi pi-fw pi-id-card', to: '/formlayout' },
                    { label: 'Input', icon: 'pi pi-fw pi-check-square', to: '/input' },
                    { label: "Float Label", icon: "pi pi-fw pi-bookmark", to: "/floatlabel" },
                    { label: "Invalid State", icon: "pi pi-fw pi-exclamation-circle", to: "invalidstate" },
                    { label: 'Button', icon: 'pi pi-fw pi-mobile', to: '/button' },
                    { label: 'Table', icon: 'pi pi-fw pi-table', to: '/table' },
                    { label: 'List', icon: 'pi pi-fw pi-list', to: '/list' },
                    { label: 'Tree', icon: 'pi pi-fw pi-share-alt', to: '/tree' },
                    { label: 'Panel', icon: 'pi pi-fw pi-tablet', to: '/panel' },
                    { label: 'Overlay', icon: 'pi pi-fw pi-clone', to: '/overlay' },
                    { label: "Media", icon: "pi pi-fw pi-image", to: "/media" },
                    { label: 'Menu', icon: 'pi pi-fw pi-bars', to: '/menu' },
                    { label: 'Message', icon: 'pi pi-fw pi-comment', to: '/messages' },
                    { label: 'File', icon: 'pi pi-fw pi-file', to: '/file' },
                    { label: 'Chart', icon: 'pi pi-fw pi-chart-bar', to: '/chart' },
                    { label: 'Misc', icon: 'pi pi-fw pi-circle-off', to: '/misc' },
                ]
            },
            {
                label: 'UI Blocks',
                items: [
                    { label: 'Free Blocks', icon: 'pi pi-fw pi-eye', to: '/blocks', badge: "NEW" },
                    { label: 'All Blocks', icon: 'pi pi-fw pi-globe', url: 'https://www.primefaces.org/primeblocks-react' }
                ]
            },
            {
                label: 'Icons',
                items: [
                    { label: 'PrimeIcons', icon: 'pi pi-fw pi-prime', to: '/icons' }
                ]
            },
            {
                label: 'Pages', icon: 'pi pi-fw pi-clone',
                items: [
                    { label: 'Crud', icon: 'pi pi-fw pi-user-edit', to: '/crud' },
                    { label: 'Timeline', icon: 'pi pi-fw pi-calendar', to: '/timeline' },
                    { label: 'Empty', icon: 'pi pi-fw pi-circle-off', to: '/empty' }
                ]
            },
            {
                label: 'Menu Hierarchy', icon: 'pi pi-fw pi-search',
                items: [
                    {
                        label: 'Submenu 1', icon: 'pi pi-fw pi-bookmark',
                        items: [
                            {
                                label: 'Submenu 1.1', icon: 'pi pi-fw pi-bookmark',
                                items: [
                                    { label: 'Submenu 1.1.1', icon: 'pi pi-fw pi-bookmark' },
                                    { label: 'Submenu 1.1.2', icon: 'pi pi-fw pi-bookmark' },
                                    { label: 'Submenu 1.1.3', icon: 'pi pi-fw pi-bookmark' },
                                ]
                            },
                            {
                                label: 'Submenu 1.2', icon: 'pi pi-fw pi-bookmark',
                                items: [
                                    { label: 'Submenu 1.2.1', icon: 'pi pi-fw pi-bookmark' },
                                    { label: 'Submenu 1.2.2', icon: 'pi pi-fw pi-bookmark' }
                                ]
                            },
                        ]
                    },
                    {
                        label: 'Submenu 2', icon: 'pi pi-fw pi-bookmark',
                        items: [
                            {
                                label: 'Submenu 2.1', icon: 'pi pi-fw pi-bookmark',
                                items: [
                                    { label: 'Submenu 2.1.1', icon: 'pi pi-fw pi-bookmark' },
                                    { label: 'Submenu 2.1.2', icon: 'pi pi-fw pi-bookmark' },
                                    { label: 'Submenu 2.1.3', icon: 'pi pi-fw pi-bookmark' },
                                ]
                            },
                            {
                                label: 'Submenu 2.2', icon: 'pi pi-fw pi-bookmark',
                                items: [
                                    { label: 'Submenu 2.2.1', icon: 'pi pi-fw pi-bookmark' },
                                    { label: 'Submenu 2.2.2', icon: 'pi pi-fw pi-bookmark' }
                                ]
                            }
                        ]
                    }
                ]
            },
            {
                label: 'Get Started',
                items: [
                    { label: 'Documentation', icon: 'pi pi-fw pi-question', command: () => { window.location = "#/documentation" } },
                    { label: 'View Source', icon: 'pi pi-fw pi-search', command: () => { window.location = "https://gitlab.com/oxton/base/oxton-admin-react" } }
                ]
            }
        ]
        demo.forEach(it => menu.push(it));
    }

    const addClass = (element, className) => {
        if (element.classList)
            element.classList.add(className);
        else
            element.className += ' ' + className;
    }

    const removeClass = (element, className) => {
        if (element.classList)
            element.classList.remove(className);
        else
            element.className = element.className.replace(new RegExp('(^|\\b)' + className.split(' ').join('|') + '(\\b|$)', 'gi'), ' ');
    }

    const wrapperClass = classNames('layout-wrapper', {
        'layout-overlay': layoutMode === 'overlay',
        'layout-static': layoutMode === 'static',
        'layout-static-sidebar-inactive': staticMenuInactive && layoutMode === 'static',
        'layout-overlay-sidebar-active': overlayMenuActive && layoutMode === 'overlay',
        'layout-mobile-sidebar-active': mobileMenuActive,
        'p-input-filled': inputStyle === 'filled',
        'p-ripple-disabled': ripple === false,
        'layout-theme-light': layoutColorMode === 'light'
    });

    return (
        <div className={wrapperClass} onClick={onWrapperClick}>
            <Tooltip ref={copyTooltipRef} target=".block-action-copy" position="bottom" content="Copied to clipboard" event="focus" />

            <AppTopbar onToggleMenuClick={onToggleMenuClick} layoutColorMode={layoutColorMode} onToggleColorScheme={onToggleColorScheme}
                mobileTopbarMenuActive={mobileTopbarMenuActive} onMobileTopbarMenuClick={onMobileTopbarMenuClick} onMobileSubTopbarMenuClick={onMobileSubTopbarMenuClick} />
            
            <div className="layout-sidebar" onClick={onSidebarClick}>
                <AppMenu model={menu} onMenuItemClick={onMenuItemClick} layoutColorMode={layoutColorMode} />
            </div>

            <div className="layout-main-container">
                <div className="layout-main">
                    {props.children}
                </div>
                {/* <Button label="Toast" icon="pi pi-user" className="w-full" onClick={() => dispatch(addMessage('teste'))} /> */}

                <AppFooter layoutColorMode={layoutColorMode} />
            </div>

            <AppConfig rippleEffect={ripple} onRippleEffect={onRipple} inputStyle={inputStyle} onInputStyleChange={onInputStyleChange}
                layoutMode={layoutMode} onLayoutModeChange={onLayoutModeChange} layoutColorMode={layoutColorMode} onColorModeChange={onColorModeChange}
                theme={theme} onThemeChange={changeTheme} />

            <CSSTransition classNames="layout-mask" timeout={{ enter: 200, exit: 200 }} in={mobileMenuActive} unmountOnExit>
                <div className="layout-mask p-component-overlay"></div>
            </CSSTransition>
            
            
            <Toast />
            
        </div>
    );

}

export default App;
